import { twMerge } from 'tailwind-merge';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

import UpInput from '../../components/UpInput';
import MapView from '../../components/MapView';
import UpButton from '../../components/UpButton';
import { toast } from '../../components/UpToast';
import useDebounce from '../../hooks/useDebounce';
import SaveAsSelect from './Address/SaveAsSelect';
import { parseMapsPlace } from '../../helpers/maps';
import BottomFooter from '../../components/BottomFooter';
import { createCustomerAddress } from '../../services/api';
import { ChevronLeftIcon, TargetIcon } from '../../assets/Icons';
import type { Address, Customer, OrderOrder } from '../../types/codexOrder';

const ADDRESS_STEP = { MAP: 'map', SEARCH: 'search' };

function AddNewAddress({
  order,
  orderId,
  customer,
  onSaveNewAddress,
}: {
  orderId: string;
  order: OrderOrder | undefined;
  customer: Customer | undefined;
  onSaveNewAddress: (newId: string) => void;
}) {
  const mapsApiKey = import.meta.env.CHECKOUT_WEB_MAPS_API_KEY;
  const [coords, setCoords] = useState({ lat: 38.88, lng: -122.42 });
  const [addressStep, setAddressStep] = useState(ADDRESS_STEP.SEARCH);

  if (!order || !customer) {
    return <Navigate to={`/${orderId}`} replace />;
  }

  return (
    <APIProvider apiKey={mapsApiKey} libraries={['places', 'geocoding']}>
      {addressStep === ADDRESS_STEP.SEARCH && (
        <SearchAddressContent
          orderId={orderId}
          setCoords={setCoords}
          setAddressStep={setAddressStep}
        />
      )}
      {addressStep === ADDRESS_STEP.MAP && (
        <MapAddressContent
          order={order}
          coords={coords}
          orderId={orderId}
          customer={customer}
          setAddressStep={setAddressStep}
          onSaveNewAddress={onSaveNewAddress}
        />
      )}
    </APIProvider>
  );
}

function SearchAddressContent({
  orderId,
  setAddressStep,
  setCoords,
}: {
  orderId: string;
  setAddressStep: (step: string) => void;
  setCoords: (coords: { lat: number; lng: number }) => void;
}) {
  const navigate = useNavigate();
  const places = useMapsLibrary('places');
  const [searchValue, setSearchValue] = useState('');
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);

  const fetchPlacePredictions = useCallback(
    async (value: string) => {
      if (!value.trim() || !places) {
        setSuggestedAddresses([]);
        return;
      }
      try {
        const response =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: value,
          });
        setSuggestedAddresses(response?.suggestions || []);
      } catch {
        toast.info('Something went wrong. Failed to load places suggestion.');
        setSuggestedAddresses([]);
      }
    },
    [places],
  );

  const debouncedFetchPlacePredictions = useDebounce(value => {
    void fetchPlacePredictions(value as string);
  }, 300);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedFetchPlacePredictions(value);
  };

  const handlePlaceSelect = async (placeId: string) => {
    if (!places) return;

    try {
      const place = new places.Place({
        id: placeId,
      });

      await place.fetchFields({
        fields: [
          'location',
          'displayName',
          'formattedAddress',
          'addressComponents',
        ],
      });

      setSuggestedAddresses([]);
      setAddressStep(ADDRESS_STEP.MAP);

      const lat = place.location?.lat();
      const lng = place.location?.lng();
      if (lat && lng) {
        setCoords({ lat, lng });
      }
    } catch {
      toast.info('Something went wrong. Could not fetch address details.');
    }
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          if (places) {
            const geocoder = new google.maps.Geocoder();
            void geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                if (status === google.maps.GeocoderStatus?.OK && results?.[0]) {
                  const placeId = results[0]?.place_id;
                  void handlePlaceSelect(placeId);
                }
              },
            );
          }
        },
        () => {
          toast.info(
            'Something went wrong. Could not fetch geolocation details.',
          );
        },
      );
    } else {
      toast.info(
        'Something went wrong. Could not load geolocation on your browser.',
      );
    }
  };

  return (
    <div className="pb-24 relative">
      <div className="flex items-center gap-x-2 px-4 py-6 bg-brand-background text-brand-text md:rounded-b-lg md:mx-2">
        <ChevronLeftIcon
          className="cursor-pointer"
          onClick={() => {
            void navigate(`/${orderId}`);
          }}
        />
        <p className="text-lg font-bold">Add New Address</p>
      </div>
      <div className="mx-2 my-3 p-4 rounded-lg bg-white">
        <div className="relative">
          <UpInput
            value={searchValue}
            label="Delivery location"
            onChange={e => {
              handleSearchChange(e.target.value);
            }}
            placeholder="Search for your location"
          />
        </div>
        {!searchValue && (
          <div
            className={twMerge(
              'w-full pt-5 mb-1',
              suggestedAddresses?.length && 'border-b border-up-black-20 pb-5',
            )}>
            <p
              onClick={handleUseCurrentLocation}
              className="text-brand-icon text-sm cursor-pointer flex items-center max-w-max">
              <TargetIcon className="mr-1" />
              <span>Use current location</span>
            </p>
          </div>
        )}
        {suggestedAddresses.length > 0 && (
          <div className="w-full mt-2">
            {suggestedAddresses.map(pred => {
              const prediction = pred?.placePrediction;
              if (!prediction) return null;
              return (
                <div
                  key={prediction?.placeId}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-up-black-20 last:border-b-0"
                  onClick={() => {
                    void handlePlaceSelect(prediction?.placeId);
                  }}>
                  <p className="text-sm font-medium">
                    {prediction?.mainText?.text}
                  </p>
                  <p className="text-xs mt-1">
                    {prediction?.secondaryText?.text}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MapAddressContent({
  order,
  coords,
  orderId,
  customer,
  setAddressStep,
  onSaveNewAddress,
}: {
  orderId: string;
  order: OrderOrder;
  customer: Customer;
  coords: { lat: number; lng: number };
  setAddressStep: (step: string) => void;
  onSaveNewAddress: (newId: string) => void;
}) {
  const navigate = useNavigate();
  const [aptValue, setAptValue] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [address, setAddress] = useState<google.maps.places.Place | null>(null);
  const formattedAddress = useMemo(() => {
    return address ? parseMapsPlace(address) : {};
  }, [address]);

  useEffect(() => {
    if (formattedAddress?.line_1) {
      setAptValue(formattedAddress.line_1);
    }
  }, [formattedAddress?.line_1]);

  const { mutate: callCreateAddress, isPending } = useMutation({
    mutationFn: (params: {
      bizId: string;
      referenceId: string;
      address: Address;
    }) => createCustomerAddress(params),
    onSuccess: res => {
      void navigate(`/${orderId}`);
      onSaveNewAddress(String(res?.address_id));
    },
    onError: err => {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.info(
        error?.response?.data?.detail ??
          'Something went wrong. Address create action failed.',
      );
    },
  });

  function onSaveAddress() {
    const addressPayload = {
      address_1: aptValue || formattedAddress.line_1,
      address_2: '',
      landmark: '',
      city: formattedAddress.city,
      sub_locality: formattedAddress.sub_locality,
      pin: formattedAddress.pin,
      lat: formattedAddress.latitude,
      lng: formattedAddress.longitude,
      phone: customer?.phone,
      tag: `${tagValue || 'Other'}__${String(Date.now())}`,
    };
    callCreateAddress({
      bizId: order?.details?.biz_id,
      referenceId: orderId,
      address: addressPayload,
    });
  }

  return (
    <div className="pb-24 relative">
      <div className="flex items-center gap-x-2 px-4 py-6 bg-brand-background text-brand-text md:rounded-b-lg md:mx-2">
        <ChevronLeftIcon
          className="cursor-pointer"
          onClick={() => {
            setAddressStep(ADDRESS_STEP.SEARCH);
          }}
        />
        <p className="text-lg font-bold">Add New Address</p>
      </div>
      <MapView coords={coords} setAddress={setAddress} />
      <div className="mx-2 my-3 p-4 rounded-lg bg-white">
        <p>{address?.displayName ?? ''}</p>
        <p className="text-sm my-3">{address?.formattedAddress ?? ''}</p>
        <p
          onClick={() => {
            setAddressStep(ADDRESS_STEP.SEARCH);
          }}
          className="text-brand-icon text-sm cursor-pointer mt-3 flex items-center max-w-max">
          <span>Change location</span>
          <ChevronLeftIcon className="w-4 h-4 rotate-180 mt-0.5" />
        </p>
      </div>
      <div className="mx-2 my-3 p-4 rounded-lg bg-white flex flex-col gap-y-6">
        <UpInput
          value={aptValue}
          placeholder="How do we reach you ?"
          label="Apartment / House / Building"
          onChange={e => {
            setAptValue(e.target.value);
          }}
        />
        <SaveAsSelect defaultSelected="Home" onSelect={setTagValue} />
      </div>
      <BottomFooter>
        <UpButton onClick={onSaveAddress} loading={isPending}>
          Save address
        </UpButton>
      </BottomFooter>
    </div>
  );
}

export default AddNewAddress;
